/* ============================================================================
   Article page behaviour. Progressive enhancement only: the contents list, the
   anchors, the share links and the related list are server-rendered, so an
   article without JavaScript loses the scroll-spy highlight, the progress bar,
   the copy-link button and the table scroll wrappers, and nothing else.
   ============================================================================ */
(function () {
  'use strict';

  var article = document.querySelector('.article-body');
  if (!article) return;

  var wide = window.matchMedia('(min-width: 1024px)');
  var toc = document.querySelector('.article-toc');

  /* Read the nav clearance rather than keep a third copy of the number. Three
     hardcoded offsets drifting apart is the bug this replaced. */
  var navClear = parseInt(
    window.getComputedStyle(document.documentElement).getPropertyValue('--article-nav-clear'), 10
  ) || 104;

  /* ── contents: scroll-spy ─────────────────────────────────────────────── */
  if (toc) {
    var links = Array.prototype.slice.call(toc.querySelectorAll('a[href^="#"]'));
    var targets = links
      .map(function (a) { return document.getElementById(a.getAttribute('href').slice(1)); })
      .filter(Boolean);

    if (targets.length) {
      /* Derived from geometry on every call, with no accumulated state.

         The previous version kept a `visible` array fed by an
         IntersectionObserver and preferred it over measurement. Any load where
         the observer lagged or stopped delivering left the highlight pinned to
         whichever heading happened to be in that array. Measured across four
         posts and six viewports, it was pinned to the first heading on 15 of
         17 scroll positions. Geometry cannot go stale, so there is nothing to
         keep in sync and no observer to gate the feature on. */
      var activeId = false;
      var paint = function () {
        var line = navClear + 8;
        var current = null;
        targets.forEach(function (t) {
          if (t.getBoundingClientRect().top <= line) current = t;
        });

        /* Everything below writes to the DOM and then reads rects back, which
           forces a synchronous layout. The section only changes every few
           hundred pixels, so on the great majority of scroll ticks there is
           nothing to do and the reads above are the whole cost. Reset on
           resize, where the boxes change size and the row can fall out of
           view without the section changing. */
        var id = current ? current.id : null;
        if (id === activeId) return;
        activeId = id;

        links.forEach(function (a) { a.removeAttribute('aria-current'); });
        /* Above the first heading the reader is in the standfirst, and no
           section is current. */
        if (!current) return;
        var link = toc.querySelector('a[href="#' + current.id + '"]');
        if (!link) return;
        link.setAttribute('aria-current', 'location');

        /* Keep the current row in view, and never scrollIntoView: that walks
           every scrolling ancestor including the window, and drags the page
           back up mid-read.

           There are two nested scrollers: the contents list, and the rail
           around it for the case where the cards alone are taller than the
           viewport. `closest('.article-toc-wrap') || closest('.article-rail')`
           looked like it handled both, but the link is always inside the list,
           so the rail branch was unreachable and a row could sit outside the
           rail's visible area with nothing to scroll it.

           Both are collected and walked innermost first. Bringing the link
           into view in the list changes where it sits inside the rail, so the
           rail is measured against the link's new position rather than a
           stale one. Only boxes that actually overflow are touched, so this
           does nothing below the two-column breakpoint where the rail is
           display: contents and neither box is height-constrained. */
        var boxes = [];
        for (var node = link.parentElement; node; node = node.parentElement) {
          if (node.scrollHeight > node.clientHeight + 2) boxes.push(node);
          if (node.classList && node.classList.contains('article-rail')) break;
        }
        boxes.forEach(function (box) {
          var lr = link.getBoundingClientRect();
          var br = box.getBoundingClientRect();
          if (lr.top < br.top || lr.bottom > br.bottom) {
            box.scrollTop += (lr.top - br.top) - (box.clientHeight - lr.height) / 2;
          }
        });
      };

      /* Throttled on a timestamp, not a boolean cleared inside
         requestAnimationFrame. The rAF form latches: it sets the flag, and
         anywhere the callback does not run the flag stays set and the
         highlight is dead for the rest of the page. paint reads at most a
         handful of rects, so 100ms is cheap. */
      var lastSpy = 0;
      var spyTail = null;
      var spyTick = function () {
        var now = new Date().getTime();
        var wait = 100 - (now - lastSpy);
        if (spyTail) { clearTimeout(spyTail); spyTail = null; }
        if (wait <= 0) {
          lastSpy = now;
          paint();
          return;
        }
        /* Trailing edge. A leading-only throttle drops the last event of a
           scroll that stops inside the window, which leaves the highlight one
           section behind, and that is exactly the state the reader is left
           looking at. */
        spyTail = setTimeout(function () {
          spyTail = null;
          lastSpy = new Date().getTime();
          paint();
        }, wait);
      };
      /* Anything that moves the layout without changing which section is
         current has to bypass the early return, or the row is left wherever
         the old geometry put it and stays there until the section changes.

         That is not hypothetical: paint runs once on the deferred script, then
         again when Manrope swaps and again on load. Both later calls saw the
         same section, returned immediately, and never remeasured the contents
         list against its new row heights, leaving the highlighted row clipped.
         Raised independently by both reviewers. */
      var repaint = function () {
        activeId = false;
        lastSpy = 0;
        paint();
      };

      window.addEventListener('scroll', spyTick, { passive: true });
      window.addEventListener('resize', repaint);
      /* Opening an in-article FAQ moves every heading below it, and the reader
         is usually still in the same section while it happens. */
      document.addEventListener('toggle', repaint, true);
      if (document.fonts && document.fonts.ready) { document.fonts.ready.then(repaint); }
      window.addEventListener('load', repaint);
      paint();
    }
  }

  /* The open state is set inline in the snippet before paint, so there is no
     expand-then-collapse flash. Here we only follow a breakpoint crossing, and
     never clobber a choice the reader has made at the current breakpoint. */
  if (toc) {
    /* Tapping a row on mobile should not leave the list covering the target. */
    toc.addEventListener('click', function (event) {
      if (wide.matches) return;
      if (event.target.closest && event.target.closest('a[href^="#"]')) toc.open = false;
    });

    var lastWide = wide.matches;
    var onBreakpoint = function () {
      if (wide.matches === lastWide) return;
      lastWide = wide.matches;
      if (!wide.matches && toc.contains(document.activeElement)) {
        var summary = toc.querySelector('summary');
        if (summary) summary.focus();
      }
      toc.open = wide.matches;
    };
    if (wide.addEventListener) wide.addEventListener('change', onBreakpoint);
    else if (wide.addListener) wide.addListener(onBreakpoint);

  }

  /* ── reading progress ─────────────────────────────────────────────────── */
  var bar = document.querySelector('.article-progress');
  if (bar) {
    var queued = false;
    var measure = function () {
      var doc = document.documentElement;
      return doc.scrollHeight - window.innerHeight;
    };
    var render = function () {
      queued = false;
      var scrollable = measure();
      /* Three live posts are under 500 words, so on a tall viewport there is
         no scrolling and therefore no progress to report. */
      if (scrollable <= 40) {
        bar.hidden = true;
        return;
      }
      bar.hidden = false;
      var done = Math.min(1, Math.max(0, window.scrollY / scrollable));
      bar.style.transform = 'scaleX(' + done.toFixed(4) + ')';
    };
    var tick = function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(render);
    };
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick);
    /* Opening an in-article FAQ, or the contents list itself, changes the
       document height. The contents list is outside .article-body. */
    document.addEventListener('toggle', tick, true);
    render();
  }

  /* ── wide tables and code blocks ──────────────────────────────────────── */
  /* The editor emits a bare <table>, so a wide one scrolls the whole page
     sideways. Wrapping it here also gives the scroll region a keyboard focus
     stop, which an author's markup never has. */
  Array.prototype.forEach.call(article.querySelectorAll('table'), function (table) {
    if (table.parentNode.classList.contains('table-scroll')) return;
    var wrap = document.createElement('div');
    wrap.className = 'table-scroll';
    table.parentNode.insertBefore(wrap, table);
    wrap.appendChild(table);
  });

  /* Measured on load and again after the webfont swaps and on resize: a table
     that only overflows once Manrope arrives would otherwise never get its
     keyboard focus stop, and one that stops overflowing would keep a pointless
     tab stop forever. */
  var regions = Array.prototype.slice.call(article.querySelectorAll('.table-scroll, pre'));
  var measureRegions = function () {
    regions.forEach(function (region) {
      var overflows = region.scrollWidth > region.clientWidth + 1;
      if (overflows) {
        region.tabIndex = 0;
        region.setAttribute('role', 'region');
        if (!region.getAttribute('aria-label')) {
          region.setAttribute('aria-label', region.querySelector('table') ? 'Table, scrollable' : 'Code, scrollable');
        }
      } else {
        region.removeAttribute('tabindex');
        region.removeAttribute('role');
        region.removeAttribute('aria-label');
      }
    });
  };
  if (regions.length) {
    measureRegions();
    window.addEventListener('load', measureRegions);
    window.addEventListener('resize', measureRegions);
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(measureRegions); }
  }

  /* ── copy link ────────────────────────────────────────────────────────── */
  var copy = document.querySelector('[data-article-copy]');
  var flag = document.querySelector('[data-article-copied]');
  if (copy) {
    if (!navigator.clipboard) {
      copy.hidden = true;
    } else {
      copy.hidden = false;
      var timer = null;
      var say = function (message) {
        if (!flag) return;
        flag.textContent = message;
        window.clearTimeout(timer);
        timer = window.setTimeout(function () { flag.textContent = ''; }, 2500);
      };

      /* Last resort when the async clipboard rejects, which it does when the
         permission is denied or the document is not in a secure context. It
         must actually put the link somewhere the reader can act on, rather
         than telling them to press a key with nothing selected. */
      var legacyCopy = function (text) {
        var field = document.createElement('textarea');
        field.value = text;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.top = '0';
        field.style.opacity = '0';
        document.body.appendChild(field);
        var ok = false;
        try {
          field.select();
          field.setSelectionRange(0, text.length);
          ok = document.execCommand('copy');
        } catch (error) {
          ok = false;
        }
        document.body.removeChild(field);
        return ok;
      };

      copy.addEventListener('click', function () {
        var url = window.location.href;
        navigator.clipboard.writeText(url).then(
          function () { say('Link copied'); },
          function () { say(legacyCopy(url) ? 'Link copied' : 'Could not copy'); }
        );
      });
    }
  }
})();
